import { platformStorage } from '../save/Storage.js';

/**
 * TestApi — internal-only `window.__platformSdkTest` shim for screenshot
 * automation.
 *
 * Installed by `bootGame()` ONLY when `?test=1` is in the URL, so this
 * API never exists on production game URLs. The shim exposes a small
 * surface that an external automation tool (e.g. Playwright via
 * `page.evaluate()`) can use to:
 *
 *   - Wait for boot to complete
 *   - Jump directly to a named scene
 *   - Inspect the current scene
 *   - Capture the live canvas as a base64 PNG
 *   - Spin up a fake match so gameplay screenshots don't need a real
 *     network connection
 *
 * Why a separate file: keeps `bootGame()` thin, and lets the test API be
 * unit-tested in isolation against a stub Phaser game.
 */

const READY_SCENE = 'MainMenu';

export class TestApi {
  /**
   * @param {object} opts
   * @param {Phaser.Game} opts.game
   * @param {import('./Screenshot.js').Screenshot} opts.screenshot
   */
  constructor(opts) {
    this.game = opts.game;
    this.screenshot = opts.screenshot;
    this._ready = false;
    this._readyResolvers = [];

    // Watch for the first time the MainMenu scene becomes active.
    this._installReadyListener();
  }

  /** Install on `window.__platformSdkTest` (no-op outside browsers). */
  install() {
    if (typeof window === 'undefined') return;
    window.__platformSdkTest = {
      // Lifecycle
      getReady: () => this.getReady(),
      isReady: () => this._ready,

      // Scene navigation
      goToScene: (name, data) => this.goToScene(name, data),
      getCurrentScene: () => this.getCurrentScene(),
      getSceneKeys: () => this.getSceneKeys(),

      // Capture
      captureScreen: (opts) => this.captureScreen(opts),

      // Mock match for gameplay screenshots
      mockMatch: (opts) => this.mockMatch(opts)
    };
  }

  /** Returns true if `?test=1` is in the URL. */
  static enabled() {
    if (typeof window === 'undefined' || !window.location || !window.location.search) return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('test') === '1';
    } catch (_) {
      return false;
    }
  }

  /** Resolve once Boot → Preload → MainMenu has finished. */
  getReady() {
    if (this._ready) return Promise.resolve(true);
    return new Promise((resolve) => this._readyResolvers.push(resolve));
  }

  goToScene(name, data) {
    if (!this.game || !this.game.scene || typeof this.game.scene.start !== 'function') {
      throw new Error('TestApi.goToScene: game.scene unavailable');
    }
    // Stop everything else first so the destination is the only active
    // scene — otherwise leftover Lobby/GameContainer would render on top.
    const all = this.game.scene.getScenes(true);
    for (const s of all) {
      const key = s.scene && s.scene.key;
      if (key && key !== name && key !== 'Boot') {
        try { this.game.scene.stop(key); } catch (_) { /* ignore */ }
      }
    }
    this.game.scene.start(name, data);
  }

  getCurrentScene() {
    if (!this.game || !this.game.scene) return null;
    const all = this.game.scene.getScenes(true);
    for (const s of all) {
      const key = s.scene && s.scene.key;
      if (key && key !== 'Boot' && key !== 'Preload') return key;
    }
    return all[0] && all[0].scene ? all[0].scene.key : null;
  }

  getSceneKeys() {
    if (!this.game || !this.game.scene || !this.game.scene.manager) return [];
    return Object.keys(this.game.scene.manager.keys || {});
  }

  /**
   * Capture the live canvas as a base64 PNG (no `data:` prefix). Returned
   * as a string so Playwright's `page.evaluate()` round-trip works
   * cleanly without Blob/ArrayBuffer marshalling.
   */
  async captureScreen(opts = {}) {
    return this.screenshot.captureBase64(opts);
  }

  /**
   * Spin up a fake match without going through the network handshake,
   * so gameplay screenshots can be captured in isolation.
   *
   * The host's display name is read from `platformStorage` so the
   * leaderboard in screenshots reflects whatever the developer has
   * stored locally — instead of the literal word "Host". The second
   * fake player keeps a stable name so CI screenshots are reproducible.
   *
   * @param {object} [opts]
   * @param {Array<{id: string, name: string}>} [opts.players]
   * @param {string} [opts.seed='screenshot-tour']
   * @param {object} [opts.settings={}]
   */
  mockMatch(opts = {}) {
    let players = opts.players;
    if (!players) {
      const hostName = platformStorage.get('playerName', 'Alice');
      players = [
        { id: 'host',  name: hostName },
        { id: 'p_two', name: 'Bob' }
      ];
    }
    const seed = opts.seed || 'screenshot-tour';
    const settings = opts.settings || {};
    this.goToScene('GameContainer', { players, seed, settings });
  }

  // ───────────────────────────────────────────────────────── internals

  _installReadyListener() {
    if (!this.game || !this.game.events) return;
    // Phaser fires 'step' once per frame; check if MainMenu is alive.
    const check = () => {
      if (this._ready) return;
      const cur = this.getCurrentScene();
      if (cur === READY_SCENE) {
        this._ready = true;
        for (const r of this._readyResolvers) r(true);
        this._readyResolvers = [];
        if (this.game.events && typeof this.game.events.off === 'function') {
          this.game.events.off('step', check);
        }
      }
    };
    if (typeof this.game.events.on === 'function') {
      this.game.events.on('step', check);
    } else {
      // No game events bus available — give up gracefully.
      this._ready = true;
      for (const r of this._readyResolvers) r(true);
      this._readyResolvers = [];
    }
  }
}
